import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Cookie, Settings, X, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

const COOKIE_CONSENT_KEY = 'cookie_consent';

export default function CookieConsent() {
  const { i18n } = useTranslation();
  
  // Normalize language detection - handle variants like 'en-US@posix'
  const getNormalizedLanguage = () => {
    const lang = i18n.resolvedLanguage || i18n.language || 'en';
    return lang.split('-')[0].split('@')[0];  // 'en-US@posix' -> 'en'
  };
  const isEnglish = getNormalizedLanguage() === 'en';
  
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be changed
    analytics: false,
    marketing: false,
  });
  
  // Focus trap refs
  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(prev => ({ ...prev, ...saved }));
      } catch (e) {
        // Invalid consent data, show banner again
        setShowBanner(true);
      }
    }
  }, []);

  // Focus trap: trap focus within modal when open
  useEffect(() => {
    if (!showBanner || !modalRef.current) return;

    // Get all focusable elements EXCLUDING disabled ones
    const focusableSelector = 
      'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';
    
    const focusableElements = modalRef.current.querySelectorAll(focusableSelector);
    
    if (focusableElements.length > 0) {
      firstFocusableRef.current = focusableElements[0];
      lastFocusableRef.current = focusableElements[focusableElements.length - 1];
      
      // Focus first enabled element after a short delay
      setTimeout(() => {
        if (firstFocusableRef.current) {
          firstFocusableRef.current.focus();
        }
      }, 100);
    }

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        // Don't close on Escape - user must make a choice
        e.preventDefault();
        return;
      }
      
      if (e.key !== 'Tab') return;

      // If focus is outside modal, redirect it back inside
      if (!modalRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        firstFocusableRef.current?.focus();
        return;
      }

      // Trap focus within modal
      if (e.shiftKey) {
        // Shift + Tab: if on first element, go to last
        if (document.activeElement === firstFocusableRef.current) {
          e.preventDefault();
          lastFocusableRef.current?.focus();
        }
      } else {
        // Tab: if on last element, go to first
        if (document.activeElement === lastFocusableRef.current) {
          e.preventDefault();
          firstFocusableRef.current?.focus();
        }
      }
    };

    // Prevent scrolling when modal is open
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showBanner]);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setShowBanner(false);
    
    // Dispatch event for analytics to listen to
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: prefs }));
  };

  const acceptAll = () => {
    const allAccepted = { essential: true, analytics: true, marketing: true };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const acceptSelected = () => {
    saveConsent(preferences);
  };

  const declineOptional = () => {
    const essentialOnly = { essential: true, analytics: false, marketing: false };
    setPreferences(essentialOnly);
    saveConsent(essentialOnly);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="presentation"
      aria-hidden="false"
    >
      <div 
        ref={modalRef}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-title"
        aria-describedby="cookie-description"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6 border-b border-stone-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-xl" aria-hidden="true">
                <Cookie className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 id="cookie-title" className="text-lg font-heading font-semibold">
                  {isEnglish ? 'Cookie Settings' : 'Evästeasetukset'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {isEnglish ? 'Manage your privacy preferences' : 'Hallinnoi tietosuoja-asetuksiasi'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p id="cookie-description" className="text-sm text-muted-foreground mb-6">
            {isEnglish 
              ? 'We use cookies to enhance your browsing experience and analyze site traffic. You can choose which cookies to accept below.'
              : 'Käytämme evästeitä parantaaksemme selauskokemustasi ja analysoidaksemme sivuston liikennettä. Voit valita alta hyväksymäsi evästeet.'
            }
          </p>

          {/* Cookie Categories */}
          <div className="space-y-4">
            {/* Essential Cookies - Always On */}
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-primary" aria-hidden="true" />
                  <span className="font-medium text-sm">
                    {isEnglish ? 'Essential Cookies' : 'Välttämättömät evästeet'}
                  </span>
                  <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                    {isEnglish ? 'Required' : 'Pakollinen'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {isEnglish 
                    ? 'Necessary for the website to function. Cannot be disabled.'
                    : 'Välttämättömiä sivuston toiminnalle. Ei voi poistaa käytöstä.'
                  }
                </p>
              </div>
              <Switch 
                checked={true} 
                disabled 
                className="data-[state=checked]:bg-primary" 
                aria-label={isEnglish ? 'Essential cookies (required, cannot be disabled)' : 'Välttämättömät evästeet (pakollinen, ei voi poistaa käytöstä)'}
              />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-blue-600" aria-hidden="true" />
                  <span className="font-medium text-sm">
                    {isEnglish ? 'Analytics Cookies' : 'Analytiikkaevästeet'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {isEnglish 
                    ? 'Help us understand how visitors use our website.'
                    : 'Auttavat meitä ymmärtämään, miten kävijät käyttävät sivustoamme.'
                  }
                </p>
              </div>
              <Switch 
                checked={preferences.analytics}
                onCheckedChange={(checked) => setPreferences(p => ({ ...p, analytics: checked }))}
                className="data-[state=checked]:bg-blue-600"
                aria-label={isEnglish ? 'Analytics cookies' : 'Analytiikkaevästeet'}
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-purple-600" aria-hidden="true" />
                  <span className="font-medium text-sm">
                    {isEnglish ? 'Marketing Cookies' : 'Markkinointievästeet'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {isEnglish 
                    ? 'Used for personalized ads and content. Currently not in use.'
                    : 'Käytetään personoituihin mainoksiin. Ei tällä hetkellä käytössä.'
                  }
                </p>
              </div>
              <Switch 
                checked={preferences.marketing}
                onCheckedChange={(checked) => setPreferences(p => ({ ...p, marketing: checked }))}
                className="data-[state=checked]:bg-purple-600"
                aria-label={isEnglish ? 'Marketing cookies' : 'Markkinointievästeet'}
              />
            </div>
          </div>

          {/* More Info Link */}
          <div className="mt-4 text-center">
            <Link 
              to="/cookies" 
              className="text-xs text-primary hover:underline"
              onClick={() => setShowBanner(false)}
            >
              {isEnglish ? 'Learn more about our cookie policy' : 'Lue lisää evästekäytännöstämme'}
            </Link>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 pt-0 flex flex-col sm:flex-row gap-3">
          <Button 
            variant="outline" 
            className="flex-1 rounded-xl"
            onClick={declineOptional}
          >
            {isEnglish ? 'Essential Only' : 'Vain välttämättömät'}
          </Button>
          <Button 
            variant="outline"
            className="flex-1 rounded-xl"
            onClick={acceptSelected}
          >
            {isEnglish ? 'Save Preferences' : 'Tallenna valinnat'}
          </Button>
          <Button 
            className="flex-1 rounded-xl bg-primary hover:bg-primary/90"
            onClick={acceptAll}
          >
            <Check className="h-4 w-4 mr-2" />
            {isEnglish ? 'Accept All' : 'Hyväksy kaikki'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Export a hook to check cookie preferences
export function useCookieConsent() {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const loadConsent = () => {
      const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (saved) {
        try {
          setConsent(JSON.parse(saved));
        } catch (e) {
          setConsent(null);
        }
      }
    };

    loadConsent();

    // Listen for consent changes
    const handleConsentChange = (e) => setConsent(e.detail);
    window.addEventListener('cookieConsentChanged', handleConsentChange);
    
    return () => window.removeEventListener('cookieConsentChanged', handleConsentChange);
  }, []);

  return consent;
}
